import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, ClipboardCheck, Clock, Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryParams } from "@/hooks/use-query-params";
import { formatDate } from "@/lib/constants";
import { portalRpc } from "@/lib/portal-rpc";
import { orpc } from "@/lib/rpc";

type EmployeeSurvey = {
  id: string;
  title: string;
  description: string | null;
  deadline: string;
  hasResponded: boolean;
  responseStatus: "pending" | "completed" | "expired";
  instanceId: string;
  /** When set, the card opens this URL instead of /my-surveys/{instanceId}.
   *  Used for onboarding surveys (CSAT/eNPS) that live under /onboarding/*. */
  overrideUrl?: string;
};

export default function MySurveys() {
  const [, navigate] = useLocation();
  const { getParam, setParam } = useQueryParams();
  const activeTab = getParam("tab") || "pending";

  const { data: rawSurveys, isLoading } = useQuery(orpc.pulseSurveys.mySurveys.queryOptions());

  const onboardingSurveysQ = useQuery(portalRpc.listMyOnboardingSurveys.queryOptions());
  const onboardingSurveys = onboardingSurveysQ.data?.surveys ?? [];

  const pulseSurveys: EmployeeSurvey[] =
    rawSurveys?.map((data) => {
      const isPastDeadline = new Date() > new Date(data.instance.deadline);
      const isCompleted = data.response.status === "completed";

      return {
        id: data.survey.id,
        title: data.survey.title,
        description: data.survey.description,
        deadline: String(data.instance.deadline),
        hasResponded: isCompleted,
        responseStatus: isCompleted ? "completed" : isPastDeadline ? "expired" : "pending",
        instanceId: data.instance.id,
      };
    }) || [];

  // Surface onboarding surveys once they've fired (pending), been answered (completed),
  // or aged past the 7-day window (expired). 'not_yet_open' stays hidden.
  const onboardingAsEmployeeSurveys: EmployeeSurvey[] = onboardingSurveys
    .filter((s) => s.status === "pending" || s.status === "completed" || s.status === "expired")
    .map((s) => ({
      id: s.kind,
      title: s.title,
      description: s.description,
      deadline: s.submittedAt ?? "",
      hasResponded: s.status === "completed",
      responseStatus:
        s.status === "completed" ? "completed" : s.status === "expired" ? "expired" : "pending",
      instanceId: s.kind,
      overrideUrl: s.url,
    }));

  const surveys: EmployeeSurvey[] = [...onboardingAsEmployeeSurveys, ...pulseSurveys];

  const pendingSurveys = surveys?.filter((s) => s.responseStatus === "pending") ?? [];
  const completedSurveys = surveys?.filter((s) => s.responseStatus === "completed") ?? [];
  const expiredSurveys = surveys?.filter((s) => s.responseStatus === "expired") ?? [];

  const handleOpenSurvey = (survey: EmployeeSurvey) => {
    navigate(survey.overrideUrl ?? `/my-surveys/${survey.instanceId}`);
  };

  const SurveyCard = ({ survey }: { survey: EmployeeSurvey }) => {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: "text-[hsl(var(--amber-state))]",
      },
      completed: {
        icon: CheckCircle2,
        color: "text-[hsl(var(--forest-pulse))]",
      },
      expired: {
        icon: AlertCircle,
        color: "text-[hsl(var(--alert-red))]",
      },
    };

    const config = statusConfig[survey.responseStatus];
    const Icon = config.icon;

    return (
      <Card
        className="cursor-pointer transition-colors bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))] rounded-md hover:bg-[hsl(var(--sand-step))]"
        onClick={() => handleOpenSurvey(survey)}
        data-testid={`card-survey-${survey.id}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <CardTitle className="text-base text-[hsl(var(--ink-primary))]">
                {survey.title}
              </CardTitle>
              {survey.description && (
                <CardDescription className="mt-1 line-clamp-2">
                  {survey.description}
                </CardDescription>
              )}
            </div>
            <div className="rounded-full p-2 bg-[hsl(var(--sand-step))]">
              <Icon className={`h-4 w-4 ${config.color}`} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-sm text-[hsl(var(--ink-muted))]">
            <span>Prazo: {formatDate(survey.deadline)}</span>
          </div>
          {survey.responseStatus === "pending" && (
            <Button
              className="w-full mt-3"
              size="sm"
              data-testid={`button-respond-survey-${survey.id}`}
            >
              Responder
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title="Pesquisas de Clima"
        description="Responda às pesquisas e compartilhe seu feedback"
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Clock className="h-5 w-5 text-[hsl(var(--amber-state))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {pendingSurveys.length}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Pendentes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--forest-pulse))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {completedSurveys.length}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Respondidas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <AlertCircle className="h-5 w-5 text-[hsl(var(--alert-red))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {expiredSurveys.length}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Expiradas</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={(tab) => setParam("tab", tab)} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pendentes ({pendingSurveys.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Respondidas ({completedSurveys.length})
          </TabsTrigger>
          <TabsTrigger value="expired" data-testid="tab-expired">
            Expiradas ({expiredSurveys.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {pendingSurveys.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardCheck className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground text-center">Nenhuma pesquisa pendente</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {pendingSurveys.map((survey) => (
                <SurveyCard key={survey.instanceId} survey={survey} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {completedSurveys.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground text-center">Nenhuma pesquisa respondida</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {completedSurveys.map((survey) => (
                <SurveyCard key={survey.instanceId} survey={survey} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="expired" className="mt-6">
          {expiredSurveys.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground text-center">Nenhuma pesquisa expirada</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {expiredSurveys.map((survey) => (
                <SurveyCard key={survey.instanceId} survey={survey} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
